import { useEffect, useId, useState } from 'react'
import { Plus, Loader2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { NativeSelect } from '@/components/ui/native-select'
import { Textarea } from '@/components/ui/textarea'
import { FormField } from '@/components/common/FormSection'
import { DJENPublication } from '@/types/publication'
import { CaseArea, CaseAreaLabels, CasePriority, CasePriorityLabels } from '@/types/case'
import { Client } from '@/types/client'
import { caseService } from '@/services/case-service'
import { clientService } from '@/services/client-service'
import { QuickClientModal } from '@/components/QuickClientModal'
import { toast } from 'sonner'

interface CreateCaseFromPublicationFormProps {
  publication: DJENPublication
  onSuccess: (caseId: string) => void
  onCancel: () => void
}

export function CreateCaseFromPublicationForm({
  publication,
  onSuccess,
  onCancel
}: CreateCaseFromPublicationFormProps) {
  const fieldId = useId()
  const [loading, setLoading] = useState(false)
  const [clients, setClients] = useState<Client[]>([])
  const [loadingClients, setLoadingClients] = useState(true)
  const [clientSearchTerm, setClientSearchTerm] = useState('')
  const [showClientDropdown, setShowClientDropdown] = useState(false)
  const [showQuickClientModal, setShowQuickClientModal] = useState(false)

  const [formData, setFormData] = useState({
    title: `Processo ${publication.numeroProcessoComMascara} - ${publication.tipoComunicacao}`,
    description: `Publicação DJEN: ${publication.tipoComunicacao}\n\nTribunal: ${publication.siglaTribunal}\nÓrgão: ${publication.nomeOrgao}\nClasse: ${publication.nomeClasse}\n\nTexto da publicação:\n${publication.texto.substring(0, 500)}${publication.texto.length > 500 ? '...' : ''}`,
    clientId: '',
    area: CaseArea.CIVIL,
    priority: CasePriority.MEDIUM,
    courtName: publication.siglaTribunal,
    courtNumber: publication.numeroProcessoComMascara,
    judgeName: '',
    estimatedValue: undefined as number | undefined,
    successFeePercentage: undefined as number | undefined,
    responsibleLawyerId: '',
    responsibleLawyerName: '',
    teamMembers: [] as string[],
    startDate: new Date().toISOString().split('T')[0],
    expectedEndDate: '',
    tags: ['DJEN', publication.tipoComunicacao]
  })

  useEffect(() => {
    loadClients()
  }, [])

  const loadClients = async () => {
    try {
      setLoadingClients(true)
      const response = await clientService.getAllClients(
        { status: 'ACTIVE' },
        { page: 1, limit: 100, sort: 'name:asc' }
      )
      setClients(response.clients)
    } catch (error) {
      console.error('Error loading clients:', error)
      toast.error('Erro ao carregar clientes')
    } finally {
      setLoadingClients(false)
    }
  }

  const getClientDisplayName = (client: Client): string => {
    if (client.clientType === 'PERSON' && client.personalData) {
      return client.personalData.fullName || 'Sem nome'
    } else if (client.clientType === 'COMPANY' && client.companyData) {
      return client.companyData.tradingName || client.companyData.companyName || 'Sem nome'
    }
    return 'Sem nome'
  }

  const filteredClients = clients.filter(client => {
    const displayName = getClientDisplayName(client).toLowerCase()
    return displayName.includes(clientSearchTerm.toLowerCase())
  })

  const handleClientSelect = (client: Client) => {
    setClientSearchTerm(getClientDisplayName(client))
    setFormData(prev => ({ ...prev, clientId: client.id }))
    setShowClientDropdown(false)
  }

  const handleClientCreated = async (newClient: Client) => {
    setClientSearchTerm(getClientDisplayName(newClient))
    setFormData(prev => ({ ...prev, clientId: newClient.id }))
    setShowQuickClientModal(false)
    await loadClients() // Refresh the client list
    toast.success('Cliente criado com sucesso!')
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!formData.clientId) {
      toast.error('Selecione um cliente')
      return
    }

    try {
      setLoading(true)

      // Clean up form data - remove empty strings for optional fields
      const cleanedData = {
        ...formData,
        expectedEndDate: formData.expectedEndDate || undefined,
        judgeName: formData.judgeName || undefined,
        responsibleLawyerId: formData.responsibleLawyerId || undefined,
        responsibleLawyerName: formData.responsibleLawyerName || undefined,
      }

      const newCase = await caseService.createFromPublication({
        ...cleanedData,
        sourcePublicationId: publication.id,
        publicationData: {
          numeroProcesso: publication.numeroProcesso,
          tribunal: publication.siglaTribunal,
          orgao: publication.nomeOrgao
        }
      })

      onSuccess(newCase.id)
    } catch (error) {
      console.error('Error creating case:', error)
      toast.error('Erro ao criar processo')
    } finally {
      setLoading(false)
    }
  }

  const required = <span className="text-destructive">*</span>

  return (
    <>
      <form onSubmit={handleSubmit} className="flex flex-col gap-4">
        {/* Dados pré-preenchidos da publicação */}
        <div className="rounded-lg border bg-muted/40 p-3">
          <h4 className="mb-2 font-medium">Dados pré-preenchidos da publicação</h4>
          <div className="space-y-1 break-words text-sm text-muted-foreground">
            <p><strong>Processo:</strong> {publication.numeroProcessoComMascara}</p>
            <p><strong>Tribunal:</strong> {publication.siglaTribunal}</p>
            <p><strong>Órgão:</strong> {publication.nomeOrgao}</p>
          </div>
        </div>

        <FormField label={<>Título do Processo {required}</>} htmlFor={`${fieldId}-title`}>
          <Input
            id={`${fieldId}-title`}
            value={formData.title}
            onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
            required
          />
        </FormField>

        <FormField label={<>Cliente {required}</>} htmlFor={`${fieldId}-client`}>
          <div className="flex flex-col gap-2 sm:flex-row">
            <div className="relative min-w-0 flex-1">
              <Input
                id={`${fieldId}-client`}
                value={clientSearchTerm}
                onChange={(e) => {
                  setClientSearchTerm(e.target.value)
                  setShowClientDropdown(true)
                }}
                onFocus={() => setShowClientDropdown(true)}
                placeholder={loadingClients ? 'Carregando clientes...' : 'Buscar cliente...'}
                disabled={loadingClients}
                autoComplete="off"
              />
              {showClientDropdown && filteredClients.length > 0 && (
                <div className="absolute z-10 mt-1 max-h-60 w-full overflow-y-auto rounded-md border bg-popover text-popover-foreground shadow-lg">
                  {filteredClients.map((client) => (
                    <button
                      key={client.id}
                      type="button"
                      onClick={() => handleClientSelect(client)}
                      className="w-full px-3 py-2 text-left text-sm hover:bg-accent hover:text-accent-foreground"
                    >
                      {getClientDisplayName(client)}
                    </button>
                  ))}
                </div>
              )}
            </div>
            <Button
              type="button"
              onClick={() => setShowQuickClientModal(true)}
              variant="outline"
              className="shrink-0"
            >
              <Plus className="size-4" aria-hidden="true" />
              Novo Cliente
            </Button>
          </div>
        </FormField>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <FormField label={<>Área do Direito {required}</>} htmlFor={`${fieldId}-area`}>
            <NativeSelect
              id={`${fieldId}-area`}
              value={formData.area}
              onChange={(e) => setFormData(prev => ({ ...prev, area: e.target.value as CaseArea }))}
              required
            >
              {Object.entries(CaseAreaLabels).map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </NativeSelect>
          </FormField>

          <FormField label="Prioridade" htmlFor={`${fieldId}-priority`}>
            <NativeSelect
              id={`${fieldId}-priority`}
              value={formData.priority}
              onChange={(e) => setFormData(prev => ({ ...prev, priority: e.target.value as CasePriority }))}
            >
              {Object.entries(CasePriorityLabels).map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </NativeSelect>
          </FormField>
        </div>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <FormField label="Tribunal" htmlFor={`${fieldId}-court`}>
            <Input
              id={`${fieldId}-court`}
              value={formData.courtName || ''}
              onChange={(e) => setFormData(prev => ({ ...prev, courtName: e.target.value }))}
            />
          </FormField>

          <FormField label="Número CNJ" htmlFor={`${fieldId}-cnj`}>
            <Input
              id={`${fieldId}-cnj`}
              value={formData.courtNumber || ''}
              onChange={(e) => setFormData(prev => ({ ...prev, courtNumber: e.target.value }))}
            />
          </FormField>
        </div>

        <FormField label="Descrição" htmlFor={`${fieldId}-description`}>
          <Textarea
            id={`${fieldId}-description`}
            value={formData.description}
            onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
            className="resize-none"
            rows={6}
          />
        </FormField>

        <div className="flex flex-col-reverse gap-2 border-t pt-4 sm:flex-row sm:justify-end">
          <Button
            type="button"
            variant="outline"
            onClick={onCancel}
            disabled={loading}
          >
            Cancelar
          </Button>
          <Button
            type="submit"
            disabled={loading || !formData.clientId}
          >
            {loading ? (
              <>
                <Loader2 className="size-4 animate-spin" aria-hidden="true" />
                Criando...
              </>
            ) : (
              'Salvar Processo'
            )}
          </Button>
        </div>
      </form>

      {showQuickClientModal && (
        <QuickClientModal
          isOpen={showQuickClientModal}
          onClose={() => setShowQuickClientModal(false)}
          onClientCreated={handleClientCreated}
        />
      )}
    </>
  )
}
